import FormWrapper from "../common/FormWrapper";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { DependencyFieldSchema } from "../../utils/schemas/initial";
import { useDispatch, useSelector } from "react-redux";
import {
  addDependencyField,
  deleteDependency,
  getAddDependencyField,
} from "../../slices/FbProperty";
import { notify } from "../../../../bmr/utils/methods/notification";
import { useEffect, useRef, useState } from "react";

const DependencyForm = ({
  fieldId,
  formSetValue,
  sectionIndex,
  fieldIndex,
  getValues,
  config,
}) => {
  if (!config) return null;

  const shadowRefComparisionType = useRef(null);
  const shadowRefFields = useRef(null);

  // Track which fieldId the currently-held data belongs to.
  // This is the single source of truth that prevents stale Redux data
  // from being applied to a different field's form.
  const loadedForFieldId = useRef(null);

  // Local dependency data — never read from the shared Redux selector
  // directly for display or form-population purposes.
  const [fieldDependencyData, setFieldDependencyData] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const reduxSections = useSelector(
    (state) => state.bmrFbSections.section.sections,
  );

  // We keep this selector ONLY so we can read isLoading for the skeleton.
  // We deliberately do NOT use getDependentField from Redux to populate
  // the form — that caused the stale-data bug.
  // const isLoading = useSelector((state) => state.bmrFbProperty.isLoading);

  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues: DependencyFieldSchema,
  });
  const dispatch = useDispatch();

  // ─── Static data ──────────────────────────────────────────────────────────

  const transformData = (data) => {
    const result = [];
    if (!data) return result;
    data.forEach((section, sIndex) => {
      section.fields?.forEach((field, fIndex) => {
        if (field.input_uuid === fieldId) return;

        result.push({
          label: `${sIndex + 1}.${fIndex + 1}. ${field.label || "Field"} (${
            field.property?.field_type || "Unknown"
          })`,
          value: field.input_uuid,
        });
      });
    });
    return result;
  };

  const currentSections =
    typeof getValues === "function"
      ? getValues("sections")
      : Array.isArray(getValues)
      ? getValues
      : reduxSections;

  const formattedData = transformData(currentSections || []);

  const comparision_type = [
    { label: "Greater Than", value: "greater_than" },
    { label: "Less Than", value: "less_than" },
  ];

  const basePath = `sections.${sectionIndex}.fields.${fieldIndex}.property.${config.basePath}`;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const clearParentFormDependency = () => {
    if (!formSetValue) return;
    formSetValue(`${basePath}.dependency`, {
      type: "",
      dependency_uuid: "",
      dependency_label: "",
    });
    formSetValue(`${basePath}.${config.greaterKey}`, "");
    formSetValue(`${basePath}.${config.lessKey}`, "");
  };

  // Apply a dependency response object to the local RHF form + parent form.
  const applyDependencyData = (data) => {
    if (data?.greater_than) {
      setValue("comparison_type", "greater_than");
      setValue("dependency_field", data.greater_than);

      if (formSetValue) {
        const targetLabel =
          formattedData.find((f) => f.value === data.greater_than)?.label ||
          "Field";
        formSetValue(`${basePath}.dependency`, {
          type: "greater",
          dependency_uuid: data.greater_than,
          dependency_label: targetLabel,
        });
        formSetValue(`${basePath}.${config.greaterKey}`, data.greater_than);
        formSetValue(`${basePath}.${config.lessKey}`, "");
      }
    } else if (data?.less_than) {
      setValue("comparison_type", "less_than");
      setValue("dependency_field", data.less_than);

      if (formSetValue) {
        const targetLabel =
          formattedData.find((f) => f.value === data.less_than)?.label ||
          "Field";
        formSetValue(`${basePath}.dependency`, {
          type: "less",
          dependency_uuid: data.less_than,
          dependency_label: targetLabel,
        });
        formSetValue(`${basePath}.${config.lessKey}`, data.less_than);
        formSetValue(`${basePath}.${config.greaterKey}`, "");
      }
    } else {
      // Server returned empty / no dependency for this field
      reset(DependencyFieldSchema);
      clearParentFormDependency();
    }
  };

  // ─── Core fix: fetch response captured directly, never from Redux selector ─
  //
  // THE REAL BUG (previous approach failed too):
  //   We set isFetching=true, dispatched, then set isFetching=false in
  //   .finally(). We gated the Redux useEffect on `if (isFetching) return`.
  //   But the gate had a race: Redux updates the selector in one render,
  //   .finally() sets isFetching=false in the NEXT render. Between those two
  //   renders the gate could be open while the selector still held stale data.
  //
  // THE ACTUAL FIX:
  //   dispatch() returns a Promise that resolves with the action payload.
  //   We await it directly and apply the data in the same callsite —
  //   completely bypassing the shared Redux selector for form population.
  //   The selector (getDependentField) is now completely unused here.

  const fetchAndApply = async (id) => {
    setIsFetching(true);
    // Clear immediately so the form is blank while loading
    reset(DependencyFieldSchema);
    clearParentFormDependency();
    setFieldDependencyData(null);
    loadedForFieldId.current = null;

    try {
      const action = await dispatch(
        getAddDependencyField({ fieldId: id, routeType: config.routeType }),
      );

      // Double-check: if the user switched fields again before this resolved,
      // discard this result — it belongs to a fieldId that is no longer active.
      if (loadedForFieldId.current !== null && loadedForFieldId.current !== id) {
        return;
      }

      const payload = action?.payload;
      const responseData = payload?.data || payload;

      loadedForFieldId.current = id;

      // Empty array = server confirmed no dependency for this field
      if (Array.isArray(responseData) && responseData.length === 0) {
        setFieldDependencyData(null);
        return;
      }

      const hasData =
        responseData &&
        typeof responseData === "object" &&
        (responseData.greater_than || responseData.less_than);

      if (hasData) {
        setFieldDependencyData(responseData);
        applyDependencyData(responseData);
      } else {
        setFieldDependencyData(null);
      }
    } catch {
      setFieldDependencyData(null);
    } finally {
      setIsFetching(false);
    }
  };

  // Re-fetch every time fieldId changes
  useEffect(() => {
    fetchAndApply(fieldId);
  }, [fieldId, config]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    const selectedField = data?.dependency_field;
    const comparisonType = data?.comparison_type;

    const payload = comparisonType && selectedField
      ? { [comparisonType]: selectedField }
      : {};

    try {
      const res = await dispatch(
        addDependencyField({
          fieldId,
          data: payload,
          routeType: config.routeType,
        }),
      ).unwrap();

      if (formSetValue) {
        if (comparisonType === "greater_than" || comparisonType === "less_than") {
          const typeStr = comparisonType === "greater_than" ? "greater" : "less";
          const targetLabel =
            formattedData.find((f) => f.value === selectedField)?.label || "Field";

          formSetValue(`${basePath}.dependency`, {
            type: typeStr,
            dependency_uuid: selectedField,
            dependency_label: targetLabel,
          });

          if (comparisonType === "greater_than") {
            formSetValue(`${basePath}.${config.greaterKey}`, selectedField);
            formSetValue(`${basePath}.${config.lessKey}`, "");
          } else {
            formSetValue(`${basePath}.${config.lessKey}`, selectedField);
            formSetValue(`${basePath}.${config.greaterKey}`, "");
          }

          // Update local state so REMOVE button appears immediately
          setFieldDependencyData({ [comparisonType]: selectedField });
        } else {
          clearParentFormDependency();
          setFieldDependencyData(null);
        }
      }

      notify.success(res?.message);

      // Re-sync with server using direct-capture pattern (no Redux selector)
      await fetchAndApply(fieldId);
    } catch (error) {
      notify.error(error?.message || "Failed to save dependency");
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDeleteDependency = async () => {
    try {
      const res = await dispatch(
        deleteDependency({ fieldId, routeType: config.routeType }),
      ).unwrap();

      // Clear everything locally right away — no need to wait for re-fetch
      setFieldDependencyData(null);
      reset(DependencyFieldSchema);
      clearParentFormDependency();
      loadedForFieldId.current = fieldId;

      notify.success(res?.message || "Dependency deleted successfully");

      // Re-sync with server to confirm
      await fetchAndApply(fieldId);
    } catch (error) {
      notify.error(error?.message || "Failed to delete dependency");
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────────

  // Driven entirely from local state — never from shared Redux selector.
  // Resets to null on every fieldId change so REMOVE never bleeds across fields.
  const hasDependency = !!(
    fieldDependencyData?.greater_than || fieldDependencyData?.less_than
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="linked-field-form">
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        defaultValues={DependencyFieldSchema}
        control={control}
      >
        {isFetching ? (
          <Box>
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: "4px", marginBottom: "1rem" }}
            />
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: "4px", marginBottom: "1rem" }}
            />
            <Skeleton
              variant="rectangular"
              height={36}
              width={120}
              sx={{ borderRadius: "6px" }}
            />
          </Box>
        ) : (
          <>
            <Box>
              {/* ── Dependency field selector ── */}
              <Controller
                name="dependency_field"
                rules={{ required: "Field is required" }}
                control={control}
                render={({ field, fieldState }) => {
                  const { error } = fieldState;
                  return (
                    <Box sx={{ marginBottom: ".5rem" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: ".3rem",
                        }}
                      >
                        <Typography>Dependency field</Typography>

                        {hasDependency && (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={handleDeleteDependency}
                            sx={{ minWidth: "110px", fontWeight: 600 }}
                          >
                            REMOVE
                          </Button>
                        )}
                      </Box>

                      <FormControl fullWidth error={!!error} size="small">
                        <Select
                          {...field}
                          value={field.value || ""}
                          displayEmpty
                          MenuProps={{
                            disablePortal: true,
                            container:
                              shadowRefFields.current?.shadowRoot ||
                              shadowRefFields.current,
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select Field
                          </MenuItem>
                          {formattedData.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <FormHelperText>{error.message}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  );
                }}
              />

              {/* ── Comparison type selector ── */}
              <Controller
                name="comparison_type"
                rules={{ required: "Comparison type is required" }}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box sx={{ marginBottom: ".5rem" }}>
                    <Typography>Comparison type</Typography>
                    <FormControl fullWidth size="small" error={!!error}>
                      <Select
                        {...field}
                        value={field.value || ""}
                        displayEmpty
                        MenuProps={{
                          disablePortal: true,
                          container:
                            shadowRefComparisionType.current?.shadowRoot ||
                            shadowRefComparisionType.current,
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select
                        </MenuItem>
                        {comparision_type.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                )}
              />
            </Box>

            <Box className="fixed bottom-0 left-0 w-full bg-red-300 p-4 shadow-md mt-2">
              <Button
                variant="contained"
                size="medium"
                type="submit"
                sx={{
                  backgroundColor: "#3571FF",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.625rem",
                  marginTop: "1rem",
                }}
              >
                Save
              </Button>
            </Box>
          </>
        )}
      </FormWrapper>
    </div>
  );
};

export default DependencyForm;